import {
  type CanvasElementFrom,
  type FormContainerVariantType,
  type PartialPropsOf,
} from '@blaze-runtime/engine-domain';
import { type PregenerationType } from '@blaze-shared/elements-domain';
import { type FormActionType } from '@blaze-shared/workflow-domain';
import { setValueByPath } from '@blaze-shared/workflow-domain';

import { isElementType } from '../../../canvas-elements/element-model/element-type-guards';
import {
  type LayoutMigration,
  type LayoutMigrationContext,
} from '../../../services/compat/migrations/layout-migration.models';
import { pregenerationTypeToAction } from '../../../utils/elements/generation.utils';

export const formContainerV2Migration: LayoutMigration = {
  mode: 'layout',
  description:
    'Migrate form container to add content origin and action, and rename model data binding key to value',
  run: (
    element: CanvasElementFrom<FormContainerVariantType>,
    context: LayoutMigrationContext,
  ): void => {
    const elements = context.layoutSnapshot.uiLayer.content;
    const fProps = element.props;
    const modelBinding = fProps.data?.find((b) => b.key === 'model');
    if (modelBinding) {
      fProps.data = [{ ...modelBinding, key: 'value' }];
    }
    const immediateParentType = elements.find((e) => e.id === element.parentNode?.id)?.type;
    const isDetailsView = isElementType(element, 'formDetails');

    // NOTE patch forms that were created before origin & action were available
    if (!fProps.content?.pregenerationType && !fProps.content?.origin) {
      if (immediateParentType === 'dialog' || immediateParentType === 'root') {
        let pregenerationType: PregenerationType | undefined;
        let action: FormActionType | undefined;
        if (isDetailsView) {
          pregenerationType = 'view';
          action = 'view';
        } else {
          pregenerationType = 'custom';
          action = 'custom';
        }

        fProps.content = {
          ...fProps.content,
          origin: 'generated',
          action,
          pregenerationType,
        };
      } else {
        fProps.content = {
          ...fProps.content,
          origin: 'drag',
          action: isDetailsView ? 'view' : 'custom',
        };
      }
    } else {
      if (!fProps.content || !('origin' in fProps.content)) {
        // Is generated if has a pregenerationType or is an immediate child of a dialog/root
        setValueByPath(
          fProps,
          'content.origin',
          (fProps.content as PartialPropsOf<'formContainer'>['content'])?.pregenerationType ||
            immediateParentType === 'dialog' ||
            immediateParentType === 'root'
            ? 'generated'
            : 'drag',
        );
      }
      if (!fProps.content || !('action' in fProps.content)) {
        const action = isDetailsView
          ? 'view'
          : fProps.content.pregenerationType
            ? pregenerationTypeToAction(fProps.content.pregenerationType)
            : 'custom';
        setValueByPath(fProps, 'content.action', action);
      }
    }
  },
};
